import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Printer, FileSpreadsheet, Download, Loader2,
  Search, X, ArrowUpDown, ChevronDown, FolderOpen, FileText,
  Scale, Upload, History, Lock
} from "lucide-react";
import { useDocEditLock, DocEditUnlockDialog, DocEditLockChip, CellEditHistory } from "@/components/DocCellEdit";
import { useEmiTracking, TrackLockDialog, TrackSheetButtons } from "@/components/EmiratesTracking";

// Months left until an expiry cell ("09/26", "2026-09-01 00:00:00", "01-09-2026"); null when not a date
const monthsLeft = (v) => {
  const s = String(v || "").trim();
  let y, m;
  let r = s.match(/^(\d{1,2})[/-](\d{2}|\d{4})$/);
  if (r) { m = +r[1]; y = +r[2] < 100 ? 2000 + +r[2] : +r[2]; }
  else if ((r = s.match(/^(\d{4})-(\d{2})-\d{2}/))) { y = +r[1]; m = +r[2]; }
  else if ((r = s.match(/^\d{1,2}[/-](\d{1,2})[/-](\d{4})$/))) { m = +r[1]; y = +r[2]; }
  if (!y || !m || m > 12) return null;
  const t = new Date();
  return (y - t.getFullYear()) * 12 + (m - (t.getMonth() + 1));
};


const MODULE_LABELS = {
  packing_list: "Packing List",
  bulk_data: "Bulk Data Sheet",
  pack_team: "Pack Team Print",
};

export default function PreviewPage() {
  const params = new URLSearchParams(window.location.search);
  const module = params.get("module");
  const entryId = params.get("id");

  const [onlyNoWt, setOnlyNoWt] = useState(params.get("nowt") === "1");
  const [loading, setLoading] = useState(true);
  const [previewData, setPreviewData] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [error, setError] = useState(null);

  // Excel search/filter/sort state
  const [searchQuery, setSearchQuery] = useState("");
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [columnFilters, setColumnFilters] = useState({});
  const [showFilterDropdown, setShowFilterDropdown] = useState(null);

  // Source files state
  const [sourceFiles, setSourceFiles] = useState([]);
  const [viewingSource, setViewingSource] = useState(null); // {index, data}
  const [activeSheet, setActiveSheet] = useState(0);          // multi-sheet Excel (USA bulk data)

  // Iter 270 — packing-list weight capture (dbl-click cell / sheet upload)
  const [editWeight, setEditWeight] = useState(null);         // {tracking, value}
  const [savingWeight, setSavingWeight] = useState(false);
  const [weightLock, setWeightLock] = useState(null);      // {tracking, value} pending
  const [weightLockPwd, setWeightLockPwd] = useState("");
  const [unlockingWeight, setUnlockingWeight] = useState(false);

  const unlockWeightEdit = async () => {
    setUnlockingWeight(true);
    try {
      await axios.post(`${API}/weight-edit/unlock`, { password: weightLockPwd },
        { headers: getAuthHeader() });
      toast.success("Weight editing unlocked for 10 minutes");
      const pending = weightLock;
      setWeightLock(null);
      setWeightLockPwd("");
      if (pending) saveWeight(pending.tracking, pending.value);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unlock failed");
    } finally { setUnlockingWeight(false); }
  };
  const weightFileRef = useRef(null);
  // On-screen cell editing (3-hour password window, audited, undoable)
  const lock = useDocEditLock();
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [pendingCell, setPendingCell] = useState(null);
  const [editCell, setEditCell] = useState(null);   // {row, cIdx, value, orig}
  const [savingCell, setSavingCell] = useState(false);
  const [cellHistOpen, setCellHistOpen] = useState(false);
  const [viewOnly, setViewOnly] = useState(false);
  const canCellEdit = !viewOnly && (module === "packing_list" || module === "bulk_data");
  const emi = useEmiTracking(module, entryId, module === "packing_list" || (module === "bulk_data" && !viewOnly), setPreviewData);
  const [cellEdits, setCellEdits] = useState([]);
  const loadCellEdits = () => canCellEdit && axios.get(`${API}/doc-preview/${module}/${entryId}/cell-edits`, { headers: getAuthHeader() })
    .then(r => setCellEdits(r.data || [])).catch(() => {});
  useEffect(() => { loadCellEdits(); }, [module, entryId]);
  const editedMap = useMemo(() => {
    const m = new Map();
    for (const e of cellEdits) {
      const k = `${e.sheet}:${e.row}:${e.col}`;
      if (!e.undone && !m.has(k)) m.set(k, e);
    }
    return m;
  }, [cellEdits]);
  const rowNoMap = useMemo(() => {
    const src = (previewData?.sheets && previewData.sheets[activeSheet]) || previewData;
    return new Map((src?.rows || []).map((r, i) => [r, i + 2]));
  }, [previewData, activeSheet]);
  // Iter 272 — weight edit-history dialog
  const [weightHistOpen, setWeightHistOpen] = useState(false);
  // Iter 285 — admin-controlled cross-doc weight sync window
  const [syncWindow, setSyncWindow] = useState("today_yesterday");
  const isAdmin = (() => {
    try { return JSON.parse(localStorage.getItem("user") || "{}").role === "admin"; }
    catch { return false; }
  })();

  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API}/settings/weight-sync-window`, { headers: getAuthHeader() })
      .then(r => setSyncWindow(r.data.key || "today_yesterday"))
      .catch(() => { /* keep default */ });
  }, []);

  useEffect(() => {
    if (!module || !entryId) {
      setError("Missing module or entry ID.");
      setLoading(false);
      return;
    }
    fetchData();
    fetchSourceFiles();
  }, [module, entryId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${API}/doc-preview/${module}/${entryId}/data`,
        { headers: getAuthHeader() }
      );
      setPreviewData(res.data);
      setViewOnly(!!res.data.view_only);

      // If PDF, also fetch the blob for inline rendering
      if (res.data.preview_type === "pdf") {
        const fileRes = await axios.get(
          `${API}/doc-preview/${module}/${entryId}/file`,
          { headers: getAuthHeader(), responseType: "blob" }
        );
        const url = URL.createObjectURL(new Blob([fileRes.data], { type: "application/pdf" }));
        setPdfBlobUrl(url);
      }
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load preview. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ---------- Actions ----------
  const fetchSourceFiles = async () => {
    try {
      const res = await axios.get(
        `${API}/doc-preview/${module}/${entryId}/source-files`,
        { headers: getAuthHeader() }
      );
      setSourceFiles(res.data.files || []);
    } catch { /* source files may not exist for older entries */ }
  };

  const handleDownloadSource = async (fileIndex, fileName) => {
    try {
      const res = await axios.get(
        `${API}/doc-preview/${module}/${entryId}/source-file/${fileIndex}`,
        { headers: getAuthHeader(), responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded: ${fileName}`);
    } catch { toast.error("Download failed. Please try again."); }
  };

  const handleViewSource = async (fileIndex) => {
    if (viewingSource?.index === fileIndex) {
      setViewingSource(null);
      return;
    }
    try {
      const res = await axios.get(
        `${API}/doc-preview/${module}/${entryId}/source-file/${fileIndex}/preview`,
        { headers: getAuthHeader() }
      );
      setViewingSource({ index: fileIndex, data: res.data });
    } catch { toast.error("Cannot preview this file."); }
  };

  const handlePrint = useCallback(() => {
    if (previewData?.preview_type === "pdf" && pdfBlobUrl) {
      const win = window.open(pdfBlobUrl, "_blank");
      if (win) win.addEventListener("load", () => win.print());
    } else {
      window.print();
    }
  }, [previewData, pdfBlobUrl]);

  const handleExportExcel = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API}/doc-preview/${module}/${entryId}/export-excel`,
        { headers: getAuthHeader(), responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(MODULE_LABELS[module] || module).replace(/\s/g, "_")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Exported to Excel!");
    } catch {
      toast.error("Export failed. Please try again.");
    }
  }, [module, entryId]);

  const handleDownload = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API}/doc-preview/${module}/${entryId}/file`,
        { headers: getAuthHeader(), responseType: "blob" }
      );
      const ext = previewData?.preview_type === "pdf" ? "pdf" : "xlsx";
      const mime = ext === "pdf" ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const url = URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = previewData?.filename || `download.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("File downloaded!");
    } catch {
      toast.error("Download failed. Please try again.");
    }
  }, [module, entryId, previewData]);

  // ---------- Excel filter/sort ----------
  const filteredAndSortedData = useMemo(() => {
    if (!previewData || previewData.preview_type !== "excel") return { rows: [], columns: [] };
    // Multi-sheet support (USA bulk data has ArticleDetails + SubPieces)
    const sheets = previewData.sheets || null;
    const source = sheets && sheets[activeSheet]
      ? sheets[activeSheet]
      : previewData;
    const columns = source.columns || [];
    let rows = [...(source.rows || [])];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      rows = rows.filter((r) => r.some((c) => c && String(c).toLowerCase().includes(q)));
    }
    Object.entries(columnFilters).forEach(([ci, fv]) => {
      if (fv && fv.trim()) {
        const idx = parseInt(ci);
        const f = fv.toLowerCase();
        rows = rows.filter((r) => r[idx] && String(r[idx]).toLowerCase().includes(f));
      }
    });
    if (sortColumn !== null) {
      rows.sort((a, b) => {
        const av = a[sortColumn], bv = b[sortColumn];
        if (!av) return sortDirection === "asc" ? 1 : -1;
        if (!bv) return sortDirection === "asc" ? -1 : 1;
        const an = parseFloat(av), bn = parseFloat(bv);
        if (!isNaN(an) && !isNaN(bn)) return sortDirection === "asc" ? an - bn : bn - an;
        return sortDirection === "asc" ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
      });
    }
    return { rows, columns };
  }, [previewData, searchQuery, columnFilters, sortColumn, sortDirection, activeSheet]);

  // Iter 270/271 — locate the weight/tracking columns per module.
  // packing_list: WEIGHT + TRACK NO. · bulk_data: PHYSICAL WEIGHT +
  // ARTICLE NUMBER. The generated files have a banner row, so the real
  // header may be the first data row.
  const weightCols = useMemo(() => {
    const cfg = module === "packing_list"
      ? { weight: "WEIGHT", track: "TRACK NO." }
      : module === "bulk_data"
        ? { weight: "PHYSICAL WEIGHT", track: "ARTICLE NUMBER" }
        : null;
    if (!cfg) return { weight: null, track: null };
    const src = (previewData?.sheets && previewData.sheets[activeSheet]) || previewData;
    if (!src) return { weight: null, track: null };
    const cols = src.columns || [];
    const rows = src.rows || [];
    const findIn = (arr, name) =>
      arr.findIndex(c => String(c || "").trim().toUpperCase() === name);
    // Iter 275 — banner-heavy sheets (ITPS header block etc.) push the real
    // header several rows down; scan the first 10 rows for the line that
    // carries BOTH the weight and tracking headers.
    let weight = findIn(cols, cfg.weight);
    let track = findIn(cols, cfg.track);
    if (weight < 0 || track < 0) {
      for (let i = 0; i < Math.min(rows.length, 10); i++) {
        const wI = findIn(rows[i], cfg.weight);
        const tI = findIn(rows[i], cfg.track);
        if (wI >= 0 && tI >= 0) { weight = wI; track = tI; break; }
      }
    }
    return { weight: weight < 0 ? null : weight, track: track < 0 ? null : track,
             weightHeader: cfg.weight, trackHeader: cfg.track };
  }, [previewData, activeSheet, module]);

  // Iter 272 — "N of M parcels missing weight" summary for the header.
  const weightSummary = useMemo(() => {
    if (weightCols.weight === null || weightCols.track === null) return null;
    const src = (previewData?.sheets && previewData.sheets[activeSheet]) || previewData;
    const rows = src?.rows || [];
    const seen = new Map();   // tracking -> has weight
    for (const r of rows) {
      const t = String(r[weightCols.track] || "").trim();
      if (!t || t.toUpperCase() === weightCols.trackHeader) continue;
      if ((previewData?.cancelled_tracks || []).includes(t.toUpperCase())) continue;
      const w = r[weightCols.weight];
      const has = w !== null && w !== undefined && String(w).trim() !== ""
        && Number(w) > 0;
      seen.set(t, (seen.get(t) || false) || has);
    }
    const total = seen.size;
    const missing = [...seen.values()].filter(v => !v).length;
    return { total, missing };
  }, [previewData, activeSheet, weightCols]);

  const weightEdits = previewData?.weight_edits || [];

  // Iter 270/271 — manual weight save: server patches database_a (so bulk
  // labels and every other panel pick it up) + the stored Excel, then we
  // reflect the value on every row of the parcel locally.
  const saveWeight = async (tracking, value) => {
    setSavingWeight(true);
    const base = module === "bulk_data" ? "bulk-data" : "packing-list";
    try {
      const resp = await axios.post(`${API}/${base}/history/${entryId}/weight`,
        { tracking, weight: value }, { headers: getAuthHeader() });
      setPreviewData(prev => {
        const clone = JSON.parse(JSON.stringify(prev));
        const src = (clone.sheets && clone.sheets[activeSheet]) || clone;
        (src.rows || []).forEach(r => {
          if (String(r[weightCols.track] || "").trim().toUpperCase()
                === tracking.toUpperCase())
            r[weightCols.weight] = String(value);
        });
        return clone;
      });
      // Iter 284 — show the cross-doc fan-out so ops know their edit
      // propagated to recent packing lists / bulk labels / bulk data.
      const cs = resp.data?.cross_sync || {};
      const synced = (cs.packing_list || 0) + (cs.bulk_data || 0) + (cs.bulk_label || 0);
      const extra = synced > 0
        ? ` · synced to ${synced} other doc${synced === 1 ? '' : 's'}`
        : '';
      toast.success(`Weight saved for ${tracking} — flows to Database A + bulk labels${extra}`);
    } catch (e) {
      if (e.response?.status === 403 && /locked/i.test(e.response?.data?.detail || "")) {
        setWeightLock({ tracking, value });
        return;
      }
      toast.error(e.response?.data?.detail || "Weight save failed");
    } finally {
      setSavingWeight(false);
      setEditWeight(null);
    }
  };

  const downloadWeightTemplate = async () => {
    try {
      const res = await axios.get(
        `${API}/packing-list/history/${entryId}/weight-template`,
        { headers: getAuthHeader(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(previewData?.filename || "packing").replace(/\.xlsx$/i, "")}_WEIGHTS.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Weight sheet downloaded — fill the WEIGHT column and upload it back");
    } catch { toast.error("Weight sheet download failed"); }
  };

  const uploadWeightSheet = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(
        `${API}/packing-list/history/${entryId}/weight-upload`, fd,
        { headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" } });
      const d = r.data;
      toast.success(
        `Weights applied to ${d.applied} parcel(s) (${d.rows_updated} rows)` +
        (d.unmatched_count ? ` · ${d.unmatched_count} unmatched` : ""));
      fetchData();   // re-render with server-patched weights
    } catch (e) {
      toast.error(e.response?.data?.detail || "Weight upload failed");
    }
  };

  const hasActiveFilters = searchQuery || Object.values(columnFilters).some((v) => v) || sortColumn !== null;

  const curSource = () => (previewData?.sheets && previewData.sheets[activeSheet]) || previewData;
  const startCellEdit = (row, cIdx) => {
    const c = { row, cIdx, value: String(row[cIdx] ?? ""), orig: String(row[cIdx] ?? "") };
    if (lock.open) setEditCell(c); else { setPendingCell(c); setUnlockOpen(true); }
  };
  const saveCell = async () => {
    const c = editCell;
    if (!c || savingCell) return;
    if (c.value === c.orig) { setEditCell(null); return; }
    const src = curSource();
    const idx = (src.rows || []).indexOf(c.row);
    if (idx < 0) { toast.error("Row not found — reload the page"); setEditCell(null); return; }
    setSavingCell(true);
    try {
      const r = await axios.post(`${API}/doc-preview/${module}/${entryId}/cell`,
        { sheet: activeSheet, row: idx + 2, col: c.cIdx + 1, value: c.value, column_name: src.columns?.[c.cIdx] || "" },
        { headers: getAuthHeader() });
      setPreviewData(prev => {
        const clone = { ...prev };
        const s = (clone.sheets && clone.sheets[activeSheet]) || clone;
        s.rows = s.rows.map((rw, i) => (i === idx ? rw.map((v, j) => (j === c.cIdx ? r.data.value : v)) : rw));
        if (clone.sheets && activeSheet === 0) clone.rows = s.rows;
        return clone;
      });
      if (!r.data.unchanged) { toast.success("Cell saved · recorded in Edit history"); loadCellEdits(); }
      setEditCell(null);
    } catch (e) {
      if (e.response?.status === 403 && /locked/i.test(e.response?.data?.detail || "")) {
        lock.setUntil(""); setPendingCell(c); setEditCell(null); setUnlockOpen(true);
      } else toast.error(e.response?.data?.detail || "Cell save failed");
    } finally { setSavingCell(false); }
  };
  const onUndone = () => { fetchData(); loadCellEdits(); };

  // ---------- Render ----------
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-slate-500 mx-auto" />
          <p className="mt-3 text-slate-600 text-sm">Loading preview...</p>
        </div>
      </div>
    );
  }

  const closePreview = () => {
    window.close();
    setTimeout(() => {
      if (window.history.length > 1) window.history.back();
      else window.location.href = "/";
    }, 200);
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <p className="text-red-600 font-medium">{error}</p>
          <Button onClick={closePreview} className="mt-4" data-testid="preview-error-close-btn">Close</Button>
        </div>
      </div>
    );
  }

  const isPdf = previewData?.preview_type === "pdf";
  const isExcel = previewData?.preview_type === "excel";

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col print:bg-white" data-testid="preview-page">
      {/* ===== ACTION BAR ===== */}
      <div className="bg-white border-b shadow-sm px-5 py-3 flex items-center justify-between print:hidden" data-testid="preview-action-bar">
        <div className="flex items-center gap-3">
          <button type="button" onClick={closePreview} aria-label="Close preview" title="Close preview"
            className="shrink-0 w-9 h-9 rounded-full border border-slate-300 bg-white flex items-center justify-center text-slate-700 hover:bg-slate-100 active:scale-95 transition-transform"
            data-testid="preview-close-btn">
            <X className="w-5 h-5" />
          </button>
          {isExcel ? (
            <FileSpreadsheet className="w-5 h-5 text-green-600" />
          ) : (
            <svg className="w-5 h-5 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
          )}
          <div>
            <h1 className="font-semibold text-sm text-slate-800">{MODULE_LABELS[module] || module}</h1>
            <p className="text-xs text-slate-500 truncate max-w-[300px]">{previewData?.filename}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={handlePrint}
            variant="outline"
            size="sm"
            className="border-blue-500 text-blue-600 hover:bg-blue-50"
            data-testid="preview-print-btn"
          >
            <Printer className="w-4 h-4 mr-1.5" />
            Print
          </Button>

          {viewOnly && <span className="rounded bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800" data-testid="preview-view-only">{module === "packing_list" ? "Your invoice · view, print & fill weights" : "View & print only"}</span>}
          {!viewOnly && <Button
            onClick={handleExportExcel}
            size="sm"
            className="bg-green-600 hover:bg-green-700 text-white"
            data-testid="preview-export-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-1.5" />
            Export to Excel
          </Button>}

          {!viewOnly && <Button
            onClick={handleDownload}
            size="sm"
            className="bg-slate-700 hover:bg-slate-800 text-white"
            data-testid="preview-download-btn"
          >
            <Download className="w-4 h-4 mr-1.5" />
            Download
          </Button>}

          {/* Iter 270/271/272 — weight capture + summary (packing list & bulk data) */}
          {isExcel && canCellEdit && (
            <>
              <DocEditLockChip lock={lock} onUnlock={() => setUnlockOpen(true)} />
              <Button size="sm" variant="outline" className="border-slate-300 text-slate-600 hover:bg-slate-50"
                      onClick={() => setCellHistOpen(true)} data-testid="preview-cell-history-btn">
                <History className="w-4 h-4 mr-1.5" /> Edit history
              </Button>
            </>
          )}
          {weightCols.weight !== null && (
            <>
              {weightSummary && (
                <span
                  className={`text-[11px] font-semibold px-2 py-1 rounded-full border ${
                    weightSummary.missing > 0
                      ? "bg-rose-50 text-rose-700 border-rose-300"
                      : "bg-emerald-50 text-emerald-700 border-emerald-300"}`}
                  title={weightSummary.missing > 0
                    ? "Parcels with no weight — bulk labels would print 0 g"
                    : "Every parcel has a weight"}
                  data-testid="preview-weight-summary"
                >
                  {weightSummary.missing > 0
                    ? `${weightSummary.missing} of ${weightSummary.total} parcels missing weight`
                    : `All ${weightSummary.total} parcels have weight`}
                </span>
              )}
              {weightSummary?.missing > 0 && (
                <button type="button" onClick={() => setOnlyNoWt(v => !v)}
                  className={`text-[11px] font-semibold px-2 py-1 rounded-full border transition-colors ${onlyNoWt
                    ? "bg-rose-600 text-white border-rose-600" : "bg-white text-rose-700 border-rose-300 hover:bg-rose-50"}`}
                  data-testid="preview-only-nowt">
                  {onlyNoWt ? "No-weight only · Show all" : "Show only no-weight"}
                </button>
              )}
              {weightEdits.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-slate-300 text-slate-600 hover:bg-slate-50"
                  onClick={() => setWeightHistOpen(true)}
                  title="Who set which weight, and when"
                  data-testid="preview-weight-history-btn"
                >
                  <History className="w-4 h-4 mr-1.5" />
                  Edits ({weightEdits.length})
                </Button>
              )}
              {/* Iter 285 — admin can widen how far back a weight edit
                  propagates into already-generated docs */}
              {isAdmin && (
                <span className="inline-flex items-center gap-1 text-[11px] text-slate-500"
                      title="How far back a weight edit fills already-generated Packing List / Bulk Data / Bulk Label files">
                  <History className="w-3.5 h-3.5 text-slate-400" />
                  Sync:
                  <select
                    value={syncWindow}
                    onChange={async (e) => {
                      const v = e.target.value;
                      setSyncWindow(v);
                      try {
                        await axios.put(`${API}/settings/weight-sync-window`,
                          { key: v }, { headers: getAuthHeader() });
                        toast.success(`Weight sync window → ${
                          v === "this_week" ? "This Week" :
                          v === "this_month" ? "This Month" : "Today + Yesterday"}`);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Failed to update sync window");
                      }
                    }}
                    className="h-6 text-[11px] border border-slate-300 rounded px-1 bg-white text-slate-700"
                    data-testid="preview-weight-sync-window"
                  >
                    <option value="today_yesterday">Today + Yesterday</option>
                    <option value="this_week">This Week</option>
                    <option value="this_month">This Month</option>
                  </select>
                </span>
              )}
            </>
          )}
          {emi.hasAny && weightCols.track !== null && <TrackSheetButtons emi={emi} />}
          {!viewOnly && module === "packing_list" && weightCols.weight !== null && (
            <>
              <Button
                size="sm"
                variant="outline"
                className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                onClick={downloadWeightTemplate}
                title="Download the fill-in weight sheet (TRACK NO. + WEIGHT), then upload it back"
                data-testid="preview-weight-template-btn"
              >
                <Scale className="w-4 h-4 mr-1.5" />
                Weight Sheet
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-indigo-500 text-indigo-700 hover:bg-indigo-50"
                onClick={() => weightFileRef.current?.click()}
                title="Upload the filled weight sheet — updates Database A + this list + bulk labels"
                data-testid="preview-weight-upload-btn"
              >
                <Upload className="w-4 h-4 mr-1.5" />
                Upload Weights
              </Button>
              <input
                ref={weightFileRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={(e) => { uploadWeightSheet(e.target.files?.[0]); e.target.value = ""; }}
                data-testid="preview-weight-upload-input"
              />
            </>
          )}

          {/* Source Files Dropdown */}
          {sourceFiles.length > 0 && (
            <div className="relative group">
              <Button
                size="sm"
                variant="outline"
                className="border-amber-500 text-amber-700 hover:bg-amber-50"
                data-testid="preview-source-files-btn"
              >
                <FolderOpen className="w-4 h-4 mr-1.5" />
                Original Files ({sourceFiles.length})
                <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
              <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-xl z-30 min-w-[280px] hidden group-hover:block">
                <div className="px-3 py-2 border-b bg-amber-50 rounded-t-lg">
                  <p className="text-xs font-semibold text-amber-800">Uploaded Source Files</p>
                </div>
                {sourceFiles.map((sf) => (
                  <div key={sf.index} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 border-b last:border-0">
                    <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <span className="text-sm truncate flex-1" title={sf.name}>{sf.name}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleViewSource(sf.index)}
                      className="h-6 px-2 text-xs text-emerald-600 hover:bg-emerald-50"
                      data-testid={`view-source-${sf.index}`}
                    >
                      View
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDownloadSource(sf.index, sf.name)}
                      className="h-6 px-2 text-xs text-blue-600 hover:bg-blue-50"
                      data-testid={`download-source-${sf.index}`}
                    >
                      Download
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ===== SOURCE FILE PREVIEW (inline) ===== */}
      {viewingSource && (
        <div className="bg-amber-50 border-b print:hidden" data-testid="source-preview-section">
          <div className="px-5 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FolderOpen className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-semibold text-amber-800">
                Original File: {sourceFiles[viewingSource.index]?.name}
              </span>
              <span className="text-xs text-amber-600">
                ({viewingSource.data?.total_rows} rows, {viewingSource.data?.total_columns} cols)
              </span>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setViewingSource(null)}
              className="h-7 text-amber-700 hover:bg-amber-100"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="max-h-[300px] overflow-auto border-t">
            <table className="w-full border-collapse text-xs" data-testid="source-preview-table">
              <thead className="bg-amber-100 sticky top-0">
                <tr>
                  <th className="border border-amber-200 px-2 py-1 text-left font-semibold text-amber-800 w-8">#</th>
                  {viewingSource.data?.columns?.map((col, i) => (
                    <th key={i} className="border border-amber-200 px-2 py-1 text-left font-semibold text-amber-800 whitespace-nowrap">
                      {col || `Col ${i + 1}`}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {viewingSource.data?.rows?.map((row, rIdx) => (
                  <tr key={rIdx} className="hover:bg-amber-50/60 even:bg-white">
                    <td className="border border-amber-100 px-2 py-1 text-amber-400">{rIdx + 1}</td>
                    {row.map((cell, cIdx) => (
                      <td key={cIdx} className="border border-amber-100 px-2 py-1 whitespace-nowrap text-gray-800">
                        {cell || ""}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== CONTENT ===== */}
      <div className="flex-1 overflow-auto print:overflow-visible">
        {/* ---- Excel Preview ---- */}
        {isExcel && (
          <div className="p-4 print:p-0">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden print:shadow-none print:rounded-none">
              {/* Sheet tabs (multi-sheet workbooks e.g. USA bulk data) */}
              {previewData.sheets && previewData.sheets.length > 1 && (
                <div className="flex items-end gap-1 bg-gray-100 border-b px-3 pt-2 print:hidden" data-testid="preview-sheet-tabs">
                  {previewData.sheets.map((s, i) => (
                    <button
                      key={s.name}
                      type="button"
                      onClick={() => {
                        setActiveSheet(i);
                        setSearchQuery("");
                        setColumnFilters({});
                        setSortColumn(null);
                      }}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-t-md border-t border-l border-r transition-colors ${
                        i === activeSheet
                          ? "bg-white border-slate-300 text-slate-900"
                          : "bg-slate-200/60 border-transparent text-slate-600 hover:bg-slate-200"
                      }`}
                      data-testid={`preview-sheet-tab-${i}`}
                    >
                      {s.name}
                      <span className="ml-1.5 text-[10px] text-slate-500">({s.total_rows})</span>
                    </button>
                  ))}
                </div>
              )}
              {/* Search / Filter Bar */}
              <div className="bg-gray-50 px-4 py-3 border-b print:hidden">
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="relative flex-1 min-w-[220px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Search all columns..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 h-9"
                      data-testid="preview-search"
                    />
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-semibold text-blue-600">{filteredAndSortedData.rows.length}</span> of {(previewData.sheets && previewData.sheets[activeSheet]) ? previewData.sheets[activeSheet].total_rows : previewData.total_rows} rows
                    {previewData.total_columns > 0 && ` | ${previewData.total_columns} cols`}
                  </div>
                  {hasActiveFilters && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setSearchQuery(""); setColumnFilters({}); setSortColumn(null); }}
                      className="text-orange-600 border-orange-300 hover:bg-orange-50"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Clear
                    </Button>
                  )}
                </div>
              </div>

              {/* Table */}
              <div className="overflow-auto max-h-[calc(100vh-160px)] print:max-h-none print:overflow-visible">
                <table className="w-full border-collapse text-sm" data-testid="preview-excel-table">
                  <thead className="bg-gray-100 sticky top-0 print:bg-gray-200">
                    <tr>
                      <th className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 bg-gray-200 w-10">#</th>
                      {filteredAndSortedData.columns.map((col, idx) => (
                        <th key={idx} className="border border-gray-300 px-2 py-1 text-left font-semibold text-gray-700 whitespace-nowrap group">
                          <div className="flex flex-col gap-1">
                            <button
                              onClick={() => {
                                if (sortColumn === idx) setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
                                else { setSortColumn(idx); setSortDirection("asc"); }
                              }}
                              className="flex items-center gap-1 hover:text-blue-600 text-left"
                            >
                              <span className="truncate max-w-[180px]">{col || `Col ${idx + 1}`}</span>
                              <ArrowUpDown className={`w-3 h-3 flex-shrink-0 ${sortColumn === idx ? "text-blue-600" : "text-gray-400 opacity-0 group-hover:opacity-100"}`} />
                            </button>
                            {/* Column filter */}
                            <div className="relative print:hidden">
                              <button
                                onClick={() => setShowFilterDropdown(showFilterDropdown === idx ? null : idx)}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                <ChevronDown className="w-3 h-3" />
                              </button>
                              {showFilterDropdown === idx && (
                                <div className="absolute top-5 left-0 z-20 bg-white border rounded shadow-lg p-2 min-w-[160px]">
                                  <Input
                                    autoFocus
                                    placeholder={`Filter ${col}...`}
                                    value={columnFilters[idx] || ""}
                                    onChange={(e) => setColumnFilters((p) => ({ ...p, [idx]: e.target.value }))}
                                    className="h-7 text-xs"
                                    onBlur={() => setTimeout(() => setShowFilterDropdown(null), 200)}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      // Iter 275 — highlight the sheet's real data-header
                      // row (Sr.No. / TRACK NO. / ARTICLE NUMBER line) in
                      // light blue and pin it above the data, even when the
                      // user sorts or filters.
                      const trackHdr = (weightCols.trackHeader || "TRACK NO.").toUpperCase();
                      const isHdr = (row) => weightCols.track !== null
                        && String(row[weightCols.track] || "").trim().toUpperCase() === trackHdr;
                      // Keep original row order normally; only pin the header
                      // to the top once the user sorts/filters (so it can't
                      // get buried mid-table).
                      const rearranged = sortColumn !== null || !!searchQuery || hasActiveFilters;
                      const baseRows = rearranged
                        ? [
                            ...filteredAndSortedData.rows.filter(isHdr),
                            ...filteredAndSortedData.rows.filter((r) => !isHdr(r)),
                          ]
                        : filteredAndSortedData.rows;
                      const displayRows = onlyNoWt && weightCols.weight !== null && weightCols.track !== null
                        ? baseRows.filter((r) => isHdr(r) || (String(r[weightCols.track] || "").trim()
                            && !(Number(r[weightCols.weight]) > 0)))
                        : baseRows;
                      const hdrRow = baseRows.find(isHdr);
                      const expIdx = hdrRow ? hdrRow.findIndex((h) => /EXP/i.test(String(h || ""))) : -1;
                      return displayRows.map((row, rIdx) => {
                      const isTableHeaderRow = isHdr(row);
                      const rowCancelled = !isTableHeaderRow && weightCols.track !== null
                        && (previewData?.cancelled_tracks || []).includes(String(row[weightCols.track] || "").trim().toUpperCase());
                      return (
                      <tr key={rIdx}
                          className={isTableHeaderRow ? "bg-sky-100 print:bg-sky-100" : rowCancelled ? "bg-rose-100 line-through text-rose-800" : "hover:bg-blue-50/40 even:bg-gray-50/60"}
                          title={rowCancelled ? "CANCELLED in Order Register" : undefined}
                          data-testid={isTableHeaderRow ? "preview-table-header-row" : rowCancelled ? `preview-cancelled-row-${rIdx}` : undefined}>
                        <td className={`border border-gray-200 px-3 py-1.5 text-xs ${isTableHeaderRow ? "text-sky-700 font-bold" : "text-gray-400"}`}>{rIdx + 1}</td>
                        {row.map((cell, cIdx) => {
                          // Iter 270/271 — weight cells are editable by
                          // double-click (packing list + bulk data);
                          // missing weights get a rose "no weight" badge.
                          const isWeightCell = weightCols.weight === cIdx;
                          const tracking = weightCols.track !== null
                            ? String(row[weightCols.track] || "").trim()
                            : "";
                          const isHeaderRow = tracking.toUpperCase()
                            === (weightCols.trackHeader || "TRACK NO.");
                          const canEditWeight = !(viewOnly && module !== "packing_list") && isWeightCell && tracking
                            && !isHeaderRow;
                          const missingWeight = canEditWeight
                            && (!cell || Number(cell) === 0);
                          const isEditing = canEditWeight
                            && editWeight?.tracking === tracking;
                          const canEditTrack = weightCols.track === cIdx && !isHeaderRow && emi.isEmi(tracking);
                          const isTrackEditing = canEditTrack && emi.edit?.old === tracking;
                          const isCellEditing = !canEditWeight && editCell?.row === row && editCell?.cIdx === cIdx;
                          const cellEditable = canCellEdit && !canEditWeight && !isWeightCell && !canEditTrack;
                          const edited = editedMap.size ? editedMap.get(`${activeSheet}:${rowNoMap.get(row)}:${cIdx + 1}`) : null;
                          const expLeft = !isTableHeaderRow && cIdx === expIdx ? monthsLeft(cell) : null;
                          const expTone = expLeft === null || expLeft >= 6 ? "" : expLeft < 0 ? "bg-rose-200 font-bold text-rose-900" : "bg-amber-200 font-semibold text-amber-900";
                          return (
                            <td key={cIdx}
                                data-expiry={expTone ? `preview-short-expiry-${rIdx}` : undefined}
                                className={`relative border border-gray-200 px-2 py-1.5 whitespace-nowrap text-gray-800 ${expTone} ${
                                  isTableHeaderRow ? "font-bold text-slate-900" : ""} ${
                                  canEditWeight ? "cursor-cell hover:bg-amber-50" : ""} ${
                                  canEditTrack ? "cursor-cell hover:bg-rose-50 text-rose-800 font-medium" : ""} ${
                                  cellEditable ? "cursor-cell hover:bg-sky-50" : ""} ${
                                  missingWeight ? "bg-rose-100/80" : ""}`}
                                title={expTone ? (expLeft < 0 ? "EXPIRED" : `Short expiry — ${expLeft} month(s) left`) : missingWeight
                                  ? "No weight — double-click to enter (needed for bulk labels)"
                                  : canEditWeight
                                    ? "Double-click to enter weight"
                                    : canEditTrack ? "Emirates parcel — double-click to enter the real tracking number"
                                    : cellEditable ? "Double-click to edit this cell" : undefined}
                                onDoubleClick={canEditTrack ? () => emi.setEdit({ old: tracking, value: /^EMI\d{5,7}$/i.test(tracking) ? "" : tracking })
                                  : canEditWeight
                                  ? () => setEditWeight({
                                      tracking,
                                      value: String(cell || "") })
                                  : cellEditable ? () => startCellEdit(row, cIdx) : undefined}
                                data-testid={canEditTrack ? `preview-track-cell-${tracking}` : canEditWeight
                                  ? `preview-weight-cell-${tracking}`
                                  : `preview-cell-${rIdx}-${cIdx}`}>
                              {edited && !isCellEditing && (
                                <span className="absolute right-0 top-0 h-2 w-2 rounded-bl-sm bg-amber-500 print:hidden"
                                      title={`Edited on screen by ${edited.by_name || edited.by} · ${new Date(edited.at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })} · was "${edited.old || "(blank)"}"`}
                                      data-testid={`preview-cell-edited-${rIdx}-${cIdx}`} />)}
                              {isTrackEditing ? (
                                <input autoFocus className="w-40 h-6 px-1 text-xs uppercase border border-rose-400 rounded" placeholder="Real tracking no."
                                  value={emi.edit.value} disabled={emi.saving}
                                  onChange={(e) => emi.setEdit((p) => ({ ...p, value: e.target.value }))}
                                  onKeyDown={(e) => { if (e.key === "Enter") emi.save(tracking, emi.edit.value); if (e.key === "Escape") emi.setEdit(null); }}
                                  onBlur={() => !emi.saving && emi.save(tracking, emi.edit.value)}
                                  data-testid="preview-track-input" />
                              ) : isCellEditing ? (
                                <input
                                  autoFocus
                                  className="min-w-[8rem] h-6 px-1 text-xs border border-sky-400 rounded"
                                  value={editCell.value}
                                  disabled={savingCell}
                                  onChange={(e) => setEditCell(prev => ({ ...prev, value: e.target.value }))}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") saveCell();
                                    if (e.key === "Escape") setEditCell(null);
                                  }}
                                  onBlur={saveCell}
                                  data-testid="preview-cell-input"
                                />
                              ) : isEditing ? (
                                <input
                                  autoFocus
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  className="w-20 h-6 px-1 text-xs border border-amber-400 rounded"
                                  value={editWeight.value}
                                  disabled={savingWeight}
                                  onChange={(e) => setEditWeight(prev =>
                                    ({ ...prev, value: e.target.value }))}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter")
                                      saveWeight(tracking, editWeight.value);
                                    if (e.key === "Escape")
                                      setEditWeight(null);
                                  }}
                                  onBlur={() => {
                                    if (savingWeight) return;
                                    if (editWeight.value === String(cell || ""))
                                      setEditWeight(null);
                                    else
                                      saveWeight(tracking, editWeight.value);
                                  }}
                                  data-testid="preview-weight-input"
                                />
                              ) : missingWeight ? (
                                <span className="inline-flex items-center gap-1 text-rose-700 text-[11px] font-semibold"
                                      data-testid={`preview-weight-missing-${tracking}`}>
                                  <span className="w-1.5 h-1.5 rounded-full bg-rose-500 inline-block" />
                                  no weight
                                </span>
                              ) : (cell || "")}
                            </td>
                          );
                        })}
                      </tr>
                      );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ---- PDF Preview ---- */}
        {isPdf && pdfBlobUrl && (
          <div className="h-full print:hidden">
            <embed
              src={pdfBlobUrl}
              type="application/pdf"
              className="w-full h-[calc(100vh-56px)]"
              data-testid="preview-pdf-embed"
            />
          </div>
        )}
      </div>

      {/* Iter 272 — weight edit history dialog */}
      {/* Iter 143 — weight-change lock: password opens a 10-minute window */}
      <Dialog open={!!weightLock} onOpenChange={(o) => { if (!o) { setWeightLock(null); setWeightLockPwd(""); } }}>
        <DialogContent className="max-w-sm" data-testid="weight-lock-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-amber-600" /> Weight already saved
            </DialogTitle>
            <DialogDescription>
              A weight is already set{weightLock ? ` for ${weightLock.tracking}` : ""}.
              Enter your password to unlock weight changes for the next 10 minutes.
            </DialogDescription>
          </DialogHeader>
          <Input type="password" value={weightLockPwd} data-testid="weight-lock-password"
            onChange={(e) => setWeightLockPwd(e.target.value)}
            placeholder="Your password" autoFocus
            onKeyDown={(e) => e.key === "Enter" && weightLockPwd && unlockWeightEdit()} />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => { setWeightLock(null); setWeightLockPwd(""); }}>Cancel</Button>
            <Button disabled={!weightLockPwd || unlockingWeight} onClick={unlockWeightEdit}
                    data-testid="weight-lock-unlock">
              {unlockingWeight ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
              Unlock & Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={weightHistOpen} onOpenChange={setWeightHistOpen}>
        <DialogContent className="max-w-2xl" data-testid="preview-weight-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-slate-600" /> Weight edit history
            </DialogTitle>
            <DialogDescription>
              Every manual or sheet-uploaded weight change for this file,
              newest first. Weights flow into Database A and bulk labels.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end mb-2">
            <Button size="sm" variant="outline"
                    disabled={weightEdits.length === 0}
                    onClick={() => {
                      const header = ["When (IST)", "By", "Email", "Tracking", "Weight"];
                      const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
                      const lines = [header.map(esc).join(",")];
                      for (const e of [...weightEdits].reverse()) {
                        let when = e.at || "";
                        try {
                          when = new Date(e.at).toLocaleString("en-IN", {
                            timeZone: "Asia/Kolkata", day: "2-digit", month: "short",
                            year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });
                        } catch { /* keep raw */ }
                        lines.push([when, e.by_name || "", e.by || "",
                                     e.track_no || "", e.weight].map(esc).join(","));
                      }
                      const blob = new Blob([lines.join("\n")], { type: "text/csv" });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `${(previewData?.filename || "weight_edits")
                        .replace(/\.xlsx$/i, "")}_WEIGHT_EDITS.csv`;
                      document.body.appendChild(a);
                      a.click();
                      // Defer cleanup — revoking the blob URL in the same
                      // tick can cancel the download in Chromium.
                      setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 1000);
                    }}
                    data-testid="preview-weight-history-csv">
              <Download className="w-4 h-4 mr-1.5" /> Export CSV
            </Button>
          </div>
          <div className="max-h-96 overflow-y-auto border rounded">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="border border-gray-200 px-3 py-2 text-left">When (IST)</th>
                  <th className="border border-gray-200 px-3 py-2 text-left">By</th>
                  <th className="border border-gray-200 px-3 py-2 text-left">Tracking</th>
                  <th className="border border-gray-200 px-3 py-2 text-right">Weight</th>
                </tr>
              </thead>
              <tbody>
                {[...weightEdits].reverse().map((e, i) => {
                  let when = e.at || "";
                  try {
                    when = new Date(e.at).toLocaleString("en-IN", {
                      timeZone: "Asia/Kolkata", day: "2-digit", month: "short",
                      hour: "2-digit", minute: "2-digit", hour12: true });
                  } catch { /* keep raw */ }
                  return (
                    <tr key={i} className="even:bg-gray-50/60"
                        data-testid={`preview-weight-history-row-${i}`}>
                      <td className="border border-gray-200 px-3 py-1.5 whitespace-nowrap text-gray-600">{when}</td>
                      <td className="border border-gray-200 px-3 py-1.5">{e.by_name || e.by || "—"}</td>
                      <td className="border border-gray-200 px-3 py-1.5 font-mono">{e.track_no}</td>
                      <td className="border border-gray-200 px-3 py-1.5 text-right font-semibold">{e.old != null && <span className="mr-1 font-normal text-slate-400 line-through">{e.old}</span>}{e.weight}</td>
                    </tr>
                  );
                })}
                {weightEdits.length === 0 && (
                  <tr><td colSpan={4} className="text-center text-gray-400 py-6">No weight edits yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
      {canCellEdit && <>
        <DocEditUnlockDialog open={unlockOpen} onClose={() => { setUnlockOpen(false); setPendingCell(null); }}
          onUnlocked={(u) => { lock.setUntil(u); setUnlockOpen(false); if (pendingCell) setEditCell(pendingCell); setPendingCell(null); }} />
        <CellEditHistory open={cellHistOpen} onClose={() => setCellHistOpen(false)} module={module} entryId={entryId}
          lockOpen={lock.open} needUnlock={() => setUnlockOpen(true)} onUndone={onUndone} />
      </>}
      <TrackLockDialog emi={emi} />
    </div>
  );
}
